import { useEffect, useState, useCallback } from "react";
import { Users, UserPlus, ArrowDownToLine, Clock, CheckCircle, ArrowUpFromLine, Gift, ShieldAlert } from "lucide-react";
import { BarChart, Bar, LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { Loader, fmt } from "@/components/shared";
import { AdminPageHeader, DateFilter } from "./adminShared";

const Tip = ({ active, payload, label }) => active && payload?.length ? (
  <div className="glass rounded-lg px-3 py-2 text-xs"><p className="text-slate-400 mb-1">{label}</p>{payload.map((p) => <p key={p.name} style={{ color: p.color }}>{p.name}: {p.value}</p>)}</div>
) : null;

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("all");
  const load = useCallback(async () => {
    const r = await api.get("/admin/dashboard", { params: { period } });
    setData(r.data);
  }, [period]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Loader />;
  const c = data.cards;
  const cards = [
    { icon: Users, label: "Total Users", value: c.total_users, accent: "text-cyan-300 bg-cyan-500/15" },
    { icon: UserPlus, label: "New Users", value: c.new_users, accent: "text-sky-300 bg-sky-500/15" },
    { icon: ArrowDownToLine, label: "Total Deposits (USDT)", value: fmt(c.total_deposits), accent: "text-blue-300 bg-blue-500/15" },
    { icon: Clock, label: "Pending Deposits", value: c.pending_deposits, accent: "text-amber-300 bg-amber-500/15" },
    { icon: CheckCircle, label: "Approved Deposits", value: c.approved_deposits, accent: "text-emerald-300 bg-emerald-500/15" },
    { icon: ArrowUpFromLine, label: "Total Withdrawals (USDT)", value: fmt(c.total_withdrawals), accent: "text-orange-300 bg-orange-500/15" },
    { icon: Clock, label: "Pending Withdrawals", value: c.pending_withdrawals, accent: "text-amber-300 bg-amber-500/15" },
    { icon: Users, label: "Direct Referrals", value: c.total_referrals, accent: "text-fuchsia-300 bg-fuchsia-500/15" },
    { icon: Gift, label: "Referral Rewards (USDT)", value: fmt(c.referral_rewards), accent: "text-pink-300 bg-pink-500/15" },
    { icon: ShieldAlert, label: "Pending KYC", value: c.pending_kyc, accent: "text-red-300 bg-red-500/15" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <AdminPageHeader eyebrow="Admin" title="Platform Overview" right={<DateFilter value={period} onChange={setPeriod} />} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map((card) => (
          <div key={card.label} data-testid={`admin-card-${card.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`} className="glass glass-hover rounded-2xl p-5">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${card.accent}`}><card.icon className="w-4.5 h-4.5" /></div>
            <p className="font-mono text-2xl font-bold text-white">{card.value}</p>
            <p className="text-xs text-slate-500 mt-1">{card.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-heading text-lg text-white mb-5">User Registrations (14d)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
              <Tooltip content={<Tip />} />
              <Line type="monotone" dataKey="users" name="Users" stroke="#00F0FF" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="referrals" name="Referrals" stroke="#a855f7" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="glass rounded-2xl p-6">
          <h3 className="font-heading text-lg text-white mb-5">Deposits & Withdrawals (14d)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
              <Tooltip content={<Tip />} cursor={{ fill: "rgba(148,163,184,0.05)" }} />
              <Bar dataKey="deposits" name="Deposits" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
              <Bar dataKey="withdrawals" name="Withdrawals" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
